/**
 * @fileoverview Componente Organism para mostrar el mensaje de nota de orden en el inbox.
 * Presenta información de la orden, productos, acciones y estado, con lógica optimizada y modular.
 * @module OrderNoteMessageMC
 * @category Component - Organism
 * @since 1.0.0
 * @version 2.1.0
 */
import React, { memo, useMemo } from 'react';
import PLATFORM_CONFIG, { getPlatformShortName } from '../../../utils/platformConfig';
import useOrderNoteActions from '../../../hooks/inbox/actions/useOrderNoteActions';
import { useTranslation } from 'react-i18next';
import MessageStatusMC from './MessageStatusMC';
import OrderNoteProductListMC from './OrderNoteProductListMC';
import OrderNoteSummaryMC from './OrderNoteSummaryMC';
import OrderNoteActionsMC from './OrderNoteActionsMC';
import CheckSuccessIcon from '../../../../components/icons/CheckSuccessIcon';
import { getRegionalConfig } from '../../../../config';
import { orderCurrencySymbol } from '../../../../services/orderService';
import useOrderNoteMessageUI from '../../../hooks/inbox/ui/useOrderNoteMessageUI';
/**
 * @typedef {Object} OrderNoteMessageMCProps
 * @property {Object} conversation - Conversación asociada al mensaje
 * @property {Object} message - Mensaje de nota de orden con resumen y productos
 */

/**
 * Componente Organism para mostrar el mensaje de nota de orden en el inbox.
 *
 * Atomic Design: Organism
 * - Compuesto por MessageStatusMC, OrderNoteProductListMC, OrderNoteSummaryMC, OrderNoteActionsMC
 * - Presenta información de la orden, productos, acciones y estado
 * - Utiliza TailwindCSS con .tw-wrapper
 * - Traducción internacionalizada con useTranslation
 * - Optimizado con memo y useMemo
 *
 * @component OrderNoteMessageMC
 * @category Component - Organism
 * @param {OrderNoteMessageMCProps} props - Propiedades del componente
 * @returns {JSX.Element} Elemento JSX renderizado
 * @example
 * <OrderNoteMessageMC conversation={conversation} message={message} />
 * @see MessageStatusMC
 * @see OrderNoteProductListMC
 * @see OrderNoteSummaryMC
 * @see OrderNoteActionsMC
 * @since 1.0.0
 * @version 2.1.0 - Optimización de diseño y documentación
 */

/**
 * Organismo: OrderNoteMessageMC
 * Mensaje de nota de orden, modularizado y optimizado.
 */
const OrderNoteMessageMC = memo(({ conversation, message }) => {
  const { t } = useTranslation('InboxMC');
  const order = useMemo(() => message.orderSummary || {}, [message.orderSummary]);
  const hideSendPdf = useMemo(() => conversation.platform === 'instagram', [conversation.platform]);
  const currency = useMemo(() => order?.currency || getRegionalConfig()?.currency, [order]);
  const currencySymbol = useMemo(() => orderCurrencySymbol(order), [order]);
  const { productsToShow, showMore, handleShowAll } = useOrderNoteMessageUI(order);
  // Hook de acciones centralizadas
  const { generateOrderLink, sendOrder, generateOrderPdf } = useOrderNoteActions({
    conversation,
    order,
  });

  // Memoiza label de método de entrega
  const deliveryMethodLabel = useMemo(() => {
    switch (order.delivery_method) {
      case 'send_order':
        return t('orderNote.delivery_home');
      case 'store_pickup':
        return t('orderNote.delivery_pickup');
      default:
        return null;
    }
  }, [order.delivery_method, t]);

  return (
    <div className="tw-wrapper tw-whitespace-pre-line tw-text-xs">
      <div className="tw-relative tw-p-4 tw-max-w-lg tw-mx-auto">
        <div className="tw-absolute tw-inset-x-0 tw-top-0 tw-flex tw-justify-center tw-items-center" style={{ transform: 'translateY(-50%)' }}>
          <CheckSuccessIcon />
        </div>
        <div className="tw-bg-[#EBF7FC] tw-rounded-[10px] tw-p-[10px] tw-flex tw-flex-col tw-items-center tw-justify-center tw-text-xs tw-mt-4">
          <span>{t('orderNote.internal_message')}</span>
          <span>{t('orderNote.send_instruction')}</span>
        </div>
        <div className="tw-mt-4 tw-text-center">
          <label className="tw-text-sm tw-font-bold">
            {order?.saveAction === 'update' ? t('orderNote.updated') : t('orderNote.generated')}
          </label>
        </div>
        <OrderNoteProductListMC
          products={order.products}
          productsToShow={productsToShow}
          showMore={showMore}
          handleShowAll={handleShowAll}
          currency={currency}
          currencySymbol={currencySymbol}
        />
        {deliveryMethodLabel && (
          <>
            <div className="tw-border-t tw-border-dashed tw-my-2" />
            <div className="tw-flex tw-justify-between">
              <label className="tw-font-bold">
                {t('orderNote.delivery')} {deliveryMethodLabel}
                {order.delivery_method === 'send_order' && (
                  <span className="tw-block tw-text-3xs">
                    {order.address},&nbsp;
                    {order.city},&nbsp;
                    {order.state},&nbsp;
                    {order.country},&nbsp;
                    {order.zip_code}
                  </span>
                )}
              </label>
            </div>
          </>
        )}
        <div className="tw-border-t tw-border-dashed tw-my-2" />
        <OrderNoteSummaryMC
          order={order}
          currency={currency}
          currencySymbol={currencySymbol}
        />
        <div className="tw-border-t tw-border-dashed tw-my-2" />
        <OrderNoteActionsMC
          order={order}
          platform={conversation.platform}
          generateOrderLink={generateOrderLink}
          sendOrder={sendOrder}
          generateOrderPdf={generateOrderPdf}
          hideSendPdf={hideSendPdf}
          platformName={getPlatformShortName(conversation.platform)}
        />
      </div>
      <MessageStatusMC message={message} platform={conversation.platform} containerClassName="tw-mt-4 tw-mb-2 tw-mr-2" />
    </div>
  );
});

OrderNoteMessageMC.displayName = 'OrderNoteMessageMC';
export default OrderNoteMessageMC;
